import currency from 'currency.js';
import { floor, isNil } from 'lodash';
import { toConvertNumber } from './calculator';
import { RadixPointType, NumberConvertOptions } from '../types/common';
import { TpslOrder, Position, Order } from '../types/trade.d';
import format from 'date-fns/format';
import isValid from 'date-fns/isValid';
import toDate from 'date-fns/toDate';
import Cookies from 'js-cookie';
import Big from 'big.js';

export function toNonExponential(num: number): string {
  const m = num.toExponential().match(/\d(?:\.(\d*))?e([+-]\d+)/);
  return num.toFixed(Math.max(0, (m?.[1] || '').length - Number(m?.[2] || 0)));
}

export const VaildNumber = (value: string | number | undefined | null, placeholder = 0) => {
  if (placeholder !== 0) {
    return isNaN(Number(value)) || Number(value) === 0 ? placeholder : Number(value);
  } else {
    return isNaN(Number(value)) ? placeholder : Number(value);
  }
};

export const formatNumber = (value: string | number, symbol = '', precision = 2) => {
  return currency((value || 0).toString(), {
    symbol: symbol,
    precision: precision,
  }).format();
};

export const formatFloor = (value: number, precision: number) => {
  return getBit(value, precision);
};

const getZero = (bit: number, value?: string) => {
  let zero = '.';
  if (value) {
    zero = value;
    for (let i = 0; i < bit - value.length; i++) {
      zero += '0';
    }
  } else {
    for (let i = 0; i < bit; i++) {
      zero += '0';
    }
    if (bit === 0) {
      zero = '';
    }
  }

  return zero;
};

export function getBit(value: number, bit: number) {
  if (isNil(value)) {
    return '';
  }
  if (bit <= 0) {
    return floor(value, bit);
  }
  let str = value.toString();
  const strIndex = str.indexOf('.');
  if (strIndex === -1) return str + getZero(bit);
  str = str.slice(0, strIndex) + '.' + getZero(bit, str.slice(strIndex + 1, strIndex + bit + 1));
  return str;
}

export const formatDisplayNumber = (val: number, precision = 2, radixPoint?: RadixPointType) => {
  if (Number.isNaN(val)) {
    return '0';
  }
  const value = Math.abs(val);
  const prec = precision > 0 ? precision : 0;

  if (value === 0) {
    return '0';
  }

  const currencySlice = (val: number, precision = 2) => {
    return toConvertNumber(currency(getBit(val, prec), { precision: precision }).toString(), {
      precision: precision,
      radixPoint: radixPoint,
    });
  };

  if (value > 0 && value < 1000) {
    return `${toConvertNumber(value.toFixed(prec), { precision: precision, radixPoint: radixPoint })}`;
    // 10万 - 100万
  } else if (value >= 1000 && value < 1000000) {
    return currencySlice(value / 1000, prec) + 'K'; // 千
  } else if (value >= 1000000 && value < 1000000000) {
    return currencySlice(value / 1000000, prec) + 'M'; // 百万
  } else if (value >= 1000000000 && value < 1000000000000) {
    return currencySlice(value / 1000000000, prec) + 'B'; // 十亿
  } else if (value >= 1000000000000) {
    return currencySlice(value / 1000000000000, prec) + 'T'; // 万亿
  }
};

export function getPositionByTpslOrder(tpslOrder: TpslOrder, positions: Position[]): Position | undefined {
  return positions.find(
    (position) =>
      position.symbol === tpslOrder.symbol &&
      position.margin_mode.toLowerCase() === tpslOrder.margin_mode.toLowerCase() &&
      position.position_side.toLowerCase() === tpslOrder.position_side.toLowerCase() &&
      position.side === (tpslOrder.order_side === 'BUY' ? 'SELL' : 'BUY')
  );
}

interface FormatPart {
  isToken: boolean;
  value: string;
}

function cleanEscapedString(input: string): string {
  const escapedStringRegExp = /^'([^]*?)'?$/;
  const doubleQuoteRegExp = /''/g;

  const matched = input.match(escapedStringRegExp);

  if (!matched) {
    return input;
  }

  return matched[1].replace(doubleQuoteRegExp, "'");
}

export function formatDate<DateType extends Date>(
  date: DateType | number,
  formatStr: string,
  options?: {
    locale?: Locale;
    weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
    firstWeekContainsDate?: number;
    useAdditionalWeekYearTokens?: boolean;
    useAdditionalDayOfYearTokens?: boolean;
  },
  lang?: string
): string {
  const list = ['Y', 'y', 'm', 'M', 'd', 'D', 'H', 'h', 'S', 's', '/', '-', ':', ' '];

  const formattingTokensRegExp = /[yYQqMLwIdDecihHKkms]o|(\w)\1*|''|'(''|[^'])+('|$)|./g;
  const unescapedLatinCharacterRegExp = /[a-zA-Z]/;

  const originalDate = toDate(date);

  if (!isValid(originalDate)) {
    throw new RangeError('Invalid time value');
  }

  const parts: FormatPart[] = formatStr.match(formattingTokensRegExp)!.map((substring) => {
    if (substring === "''") {
      return { isToken: false, value: "'" };
    }

    const firstCharacter = substring[0];
    if (firstCharacter === "'") {
      return { isToken: false, value: cleanEscapedString(substring) };
    }

    if (list.includes(firstCharacter)) {
      return { isToken: true, value: substring };
    }

    if (firstCharacter.match(unescapedLatinCharacterRegExp)) {
      throw new RangeError('Format string contains an unescaped latin alphabet character `' + firstCharacter + '`');
    }

    return { isToken: false, value: substring };
  });

  const flag = parts.some((item) => !item.isToken);
  const formatValues = parts.map((item) => item.value.toLowerCase());
  const hasY = formatValues.includes('yyyy');
  const hasM = formatValues.includes('mm');
  const hasD = formatValues.includes('dd');
  const has_ = formatValues.includes('-');

  if (!flag && hasY && hasM && hasD && !has_) {
    const locale = lang || Cookies.get('lang') || 'en';
    const str = formatStr.split(' ');
    const dateFormat = str[1]
      ? (locale === 'zh-tw' ? 'yyyy/MM/dd' : 'MM/dd/yyyy') + ' ' + str[1]
      : locale === 'zh-tw'
        ? 'yyyy/MM/dd'
        : 'MM/dd/yyyy';
    return format(originalDate, dateFormat, options);
  }

  return format(originalDate, formatStr, options);
}

export const formatNumberToUsd = (value: string | number | undefined, precision = 2) => {
  return value ? (String(value) === '--' ? value : `$${new Big(value).round(precision, 0).toString()}`) : '--';
};

export const getTradeTypeFromOrderItem = (ordeItem: { inst_type?: string }) => {
  return {
    isFutures: !ordeItem.inst_type || ordeItem.inst_type === 'SWAP',
    isSpot: !!(ordeItem.inst_type && ordeItem.inst_type === 'SPOT'),
  };
};

interface CancelUnionOrders {
  inst_type?: string;
  order_id?: number;
  client_order_id?: string;
}

export const createCancelAllUnionOrders = (orders: CancelUnionOrders[], marketId?: string) => {
  if (orders.length === 0) {
    return { param: [] };
  }

  const arr = [];

  const spotOrders = orders.filter((order) => order.inst_type === 'SPOT');
  const futuresOrders = orders.filter((order) => order.inst_type === 'SWAP');

  const spotObj = {
    inst_type: 'SPOT',
    symbol: marketId || '',
    order_ids: spotOrders.map((order) => order.order_id).filter((item) => !!item),
    client_order_ids: spotOrders.map((order) => order.client_order_id).filter((item) => !!item),
  };

  if (spotObj.order_ids.length !== 0 || spotObj.client_order_ids.length !== 0) {
    arr.push(spotObj);
  }

  const futuresObj = {
    inst_type: 'SWAP',
    symbol: marketId || '',
    order_ids: futuresOrders.map((order) => order.order_id).filter((item) => !!item),
    client_order_ids: futuresOrders.map((order) => order.client_order_id).filter((item) => !!item),
  };

  if (futuresObj.order_ids.length !== 0 || futuresObj.client_order_ids.length !== 0) {
    arr.push(futuresObj);
  }

  return { param: arr };
};

export const getValueClassName = (num: string | number) => {
  const number = Number(num);
  if (!number) return '';
  return number > 0 ? 'text-light-green dark:text-dark-green' : 'text-light-red dark:text-dark-red';
};

export const pnlFormatNumber = (
  num: string | number,
  options: NumberConvertOptions,
  hidePosNeg?: boolean,
  symbol = ''
) => {
  const number = Number(num);
  if (!number) return num === '0' ? symbol + '0' : '--';
  const sign = !hidePosNeg ? (number > 0 ? '+' + symbol : '-' + symbol) : symbol;
  return sign + toConvertNumber(new Big(Math.abs(VaildNumber(number))).toString(), options);
};
